import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAppState, ACTION_TYPES } from '../hooks/useAppState';
import { getTheme } from '../lib/theme';
import * as api from '../lib/api';
import { Shield, Building2, LayoutDashboard, FileText, Users, ClipboardList, BarChart3, ListChecks, FolderOpen, Plus, Sun, Moon, Trash2, Menu, X, LogOut, CreditCard } from 'lucide-react';

const NAV_ITEMS = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Panel' },
  { path: '/politica', icon: FileText, label: 'Política' },
  { path: '/trabajadores', icon: Users, label: 'Trabajadores' },
  { path: '/cuestionarios', icon: ClipboardList, label: 'Cuestionarios' },
  { path: '/resultados', icon: BarChart3, label: 'Resultados' },
  { path: '/programa', icon: ListChecks, label: 'Programa' },
  { path: '/evidencia', icon: FolderOpen, label: 'Evidencia' },
];

export default function Layout() {
  const { state, dispatch } = useAppState();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const t = getTheme(state.darkMode);
  const dark = state.darkMode;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const [planInfo, setPlanInfo] = useState(null);

  const openPlanPopup = async () => {
    setShowPlan(true);
    try {
      const sub = await api.getSubscription();
      setPlanInfo(sub);
    } catch (e) { setPlanInfo({ error: true }); }
  };

  // Close sidebar on route change (mobile)
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Guard: redirect if no empresa/centro
  useEffect(() => {
    if (!state.empresa) navigate('/', { replace: true });
    else if (state.centrosTrabajo.length === 0) navigate('/registro-centro', { replace: true });
  }, [state.empresa, state.centrosTrabajo, navigate]);

  if (!state.empresa || state.centrosTrabajo.length === 0) return null;

  const sidebar = (
    <nav style={{
      width: 240, flexShrink: 0, display: 'flex', flexDirection: 'column',
      background: t.sidebar, borderRight: `1px solid ${t.sidebarBorder}`,
      overflowY: 'auto', height: '100%',
    }}>
      {/* Logo + mobile close */}
      <div style={{ padding: '20px 16px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '1.1rem', fontWeight: 700, color: t.accent }}>
          <Shield size={20} /> NOM-035
        </div>
        <button onClick={() => setMobileOpen(false)} className="mobile-only" style={{ background: 'none', border: 'none', color: t.textMuted, cursor: 'pointer', padding: 4, display: 'none' }}><X size={20} /></button>
      </div>

      {/* Empresa */}
      <div style={{ padding: '8px 16px 12px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: `1px solid ${t.border}`, marginBottom: 8 }}>
        <Building2 size={16} style={{ color: t.textMuted, flexShrink: 0 }} />
        <div style={{ overflow: 'hidden' }}>
          <div style={{ fontWeight: 600, fontSize: '0.82rem', color: t.textStrong, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{state.empresa.nombre}</div>
          <div style={{ fontSize: '0.72rem', color: t.textFaint }}>{state.centrosTrabajo.length} centro{state.centrosTrabajo.length !== 1 ? 's' : ''}</div>
        </div>
      </div>

      {/* Centro selector */}
      <div style={{ padding: '0 12px', marginBottom: 12 }}>
        <div style={{ fontSize: '0.65rem', color: t.textFaint, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 6, paddingLeft: 4 }}>Centro de trabajo</div>
        {state.centrosTrabajo.map((ct, idx) => (
          <button key={ct.id} onClick={() => dispatch({ type: ACTION_TYPES.SET_CENTRO_ACTUAL, payload: idx })}
            style={{ width: '100%', textAlign: 'left', padding: '7px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', marginBottom: 2, background: idx === state.centroActualIdx ? t.accentLight : 'transparent', color: idx === state.centroActualIdx ? t.textLink : t.textMuted, fontSize: '0.8rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: idx === state.centroActualIdx ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ct.nombreCentro}</span>
            <span style={{ fontSize: '0.68rem', color: t.textFaint, flexShrink: 0, marginLeft: 6 }}>{ct.numTrabajadores}</span>
          </button>
        ))}
        <button onClick={() => navigate('/registro-centro')} style={{ width: '100%', textAlign: 'left', padding: '7px 10px', borderRadius: 6, border: `1px dashed ${t.accentBorder}`, background: 'transparent', color: t.textLink, fontSize: '0.75rem', cursor: 'pointer', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} /> Agregar centro
        </button>
      </div>

      {/* Nav */}
      <div style={{ padding: '8px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {NAV_ITEMS.map(item => (
          <NavLink key={item.path} to={item.path}
            style={({ isActive }) => ({ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', background: isActive ? t.accentLight : 'none', border: 'none', borderRadius: 8, textDecoration: 'none', color: isActive ? t.textLink : t.textMuted, fontSize: '0.86rem', fontWeight: isActive ? 600 : 400 })}>
            <item.icon size={18} /> {item.label}
          </NavLink>
        ))}
      </div>

      {/* Footer */}
      <div style={{ marginTop: 'auto', padding: '12px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {user && (
          <div style={{ padding: '8px 10px', background: dark ? 'rgba(255,255,255,0.03)' : '#f9fafb', borderRadius: 8, marginBottom: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: t.textStrong, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{user.displayName || user.email}</div>
              {user.email === 'contacto@perspicaz.com.mx' && <span style={{ fontSize: '0.55rem', fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: '#f59e0b', color: '#fff', letterSpacing: '0.05em' }}>DEV</span>}
            </div>
            <div style={{ fontSize: '0.65rem', color: t.textFaint, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
          </div>
        )}
        <button onClick={openPlanPopup}
          style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid ${t.border}`, background: dark ? 'rgba(255,255,255,0.04)' : '#f3f4f6', color: t.textMuted, fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <CreditCard size={14} /> Mi plan
        </button>
        <button onClick={() => dispatch({ type: ACTION_TYPES.SET_DARK_MODE, payload: !dark })}
          style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid ${t.border}`, background: dark ? 'rgba(255,255,255,0.04)' : '#f3f4f6', color: t.textMuted, fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          {dark ? <Sun size={14} /> : <Moon size={14} />} {dark ? 'Modo claro' : 'Modo oscuro'}
        </button>
        <button onClick={async () => { await logout(); dispatch({ type: ACTION_TYPES.CLEAR_ALL }); navigate('/login'); }}
          style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid ${t.border}`, background: 'transparent', color: t.textMuted, fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <LogOut size={14} /> Cerrar sesión
        </button>
      </div>
    </nav>
  );

  return (
    <>
      {/* Responsive CSS */}
      <style>{`
        @media (max-width: 768px) {
          .layout-sidebar { position: fixed !important; top: 0; left: 0; bottom: 0; z-index: 1000; transform: translateX(-100%); transition: transform 0.25s ease; }
          .layout-sidebar.open { transform: translateX(0); }
          .layout-overlay { display: none; position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 999; }
          .layout-overlay.open { display: block; }
          .layout-main { padding: 16px 16px !important; }
          .mobile-topbar { display: flex !important; }
          .mobile-only { display: block !important; }
        }
        @media (min-width: 769px) {
          .mobile-topbar { display: none !important; }
        }
      `}</style>

      <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
        {/* Mobile overlay */}
        <div className={`layout-overlay ${mobileOpen ? 'open' : ''}`} onClick={() => setMobileOpen(false)} />

        {/* Sidebar */}
        <div className={`layout-sidebar ${mobileOpen ? 'open' : ''}`} style={{ display: 'flex' }}>
          {sidebar}
        </div>

        {/* Main */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Mobile top bar */}
          <div className="mobile-topbar" style={{ display: 'none', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: `1px solid ${t.border}`, background: t.sidebar, flexShrink: 0 }}>
            <button onClick={() => setMobileOpen(true)} style={{ background: 'none', border: 'none', color: t.textStrong, cursor: 'pointer', padding: 4 }}><Menu size={22} /></button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.95rem', fontWeight: 700, color: t.accent }}><Shield size={16} /> NOM-035</div>
            <div style={{ marginLeft: 'auto', fontSize: '0.72rem', color: t.textMuted }}>{state.centrosTrabajo[state.centroActualIdx]?.nombreCentro}</div>
          </div>

          <main className="layout-main" style={{ flex: 1, padding: '32px 40px', overflowY: 'auto' }}>
            <Outlet />
          </main>
        </div>
      </div>

      {/* Plan popup */}
      {showPlan && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }} onClick={() => setShowPlan(false)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: t.textStrong, margin: 0 }}>Mi plan</h3>
              <button onClick={() => setShowPlan(false)} style={{ background: 'none', border: 'none', color: t.textMuted, cursor: 'pointer' }}><X size={18} /></button>
            </div>
            {!planInfo ? (
              <div style={{ textAlign: 'center', padding: 20, color: t.textMuted, fontSize: '0.85rem' }}>Cargando...</div>
            ) : planInfo.dev_mode ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '3px 8px', borderRadius: 5, background: '#f59e0b', color: '#fff' }}>DEV</span>
                  <span style={{ fontSize: '1rem', fontWeight: 700, color: t.textStrong }}>{planInfo.plan_name || 'Plan Empresarial'}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${t.border}` }}>
                    <span style={{ fontSize: '0.82rem', color: t.textMuted }}>Trabajadores</span>
                    <span style={{ fontSize: '0.82rem', fontWeight: 600, color: t.textStrong }}>Ilimitados</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${t.border}` }}>
                    <span style={{ fontSize: '0.82rem', color: t.textMuted }}>Centros de trabajo</span>
                    <span style={{ fontSize: '0.82rem', fontWeight: 600, color: t.textStrong }}>Ilimitados</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                    <span style={{ fontSize: '0.82rem', color: t.textMuted }}>Estado</span>
                    <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#10b981' }}>Activo (modo desarrollo)</span>
                  </div>
                </div>
                <button onClick={() => { setShowPlan(false); navigate('/planes?upgrade=true'); }} style={{ marginTop: 16, width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${t.border}`, background: 'transparent', color: t.textMuted, fontSize: '0.82rem', cursor: 'pointer' }}>Cambiar plan</button>
              </div>
            ) : planInfo.active ? (
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: t.textStrong, marginBottom: 16 }}>{planInfo.plan_name || planInfo.plan_key || 'Plan activo'}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${t.border}` }}>
                    <span style={{ fontSize: '0.82rem', color: t.textMuted }}>Trabajadores</span>
                    <span style={{ fontSize: '0.82rem', fontWeight: 600, color: t.textStrong }}>{planInfo.max_trabajadores >= 99999 ? 'Ilimitados' : `Hasta ${planInfo.max_trabajadores}`}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${t.border}` }}>
                    <span style={{ fontSize: '0.82rem', color: t.textMuted }}>Centros de trabajo</span>
                    <span style={{ fontSize: '0.82rem', fontWeight: 600, color: t.textStrong }}>{planInfo.max_centros}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                    <span style={{ fontSize: '0.82rem', color: t.textMuted }}>Estado</span>
                    <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#10b981' }}>Activo</span>
                  </div>
                </div>
                <button onClick={() => { setShowPlan(false); navigate('/planes?upgrade=true'); }} style={{ marginTop: 16, width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${t.border}`, background: 'transparent', color: t.textMuted, fontSize: '0.82rem', cursor: 'pointer' }}>Cambiar plan</button>
              </div>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '0.88rem', color: t.textMuted, marginBottom: 16 }}>No tienes un plan activo.</p>
                <button onClick={() => { setShowPlan(false); navigate('/planes'); }} style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: t.accent, color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>Ver planes</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sync error toast */}
      {state.syncError && (
        <div style={{
          position: 'fixed', bottom: 16, right: 16, zIndex: 3000, maxWidth: 320,
          padding: '12px 14px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca',
          color: '#991b1b', fontSize: '0.8rem', lineHeight: 1.4, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          display: 'flex', alignItems: 'flex-start', gap: 8,
        }}>
          <span style={{ flex: 1 }}>No se pudo guardar el último cambio. Verifica tu conexión e inténtalo de nuevo.</span>
          <button onClick={() => dispatch({ type: ACTION_TYPES.SET_SYNC_ERROR, payload: null })}
            style={{ background: 'none', border: 'none', color: '#991b1b', cursor: 'pointer', padding: 0, flexShrink: 0, display: 'flex' }}>
            <X size={14} />
          </button>
        </div>
      )}
    </>
  );
}
